import localforage from 'localforage'
import throttle from 'lodash/throttle'
import { onSnapshot } from 'mobx-state-tree'

import { appConfig } from '../config'
import { TAuthModel } from '../models/AuthModel'
import {
  AUTH_COOKIE_EXPIRY_DAYS,
  readAuthCookie,
  refreshAuthCookie,
  removeAuthCookie,
  writeAuthCookie,
} from './authCookie'
import { isDefined } from './isDefined'
import { logPersistence } from './logger'

const ONE_DAY_MS = 24 * 60 * 60 * 1000

/** Log out an open-but-idle tab after this long without any activity */
const AUTH_IDLE_TIMEOUT_MS = AUTH_COOKIE_EXPIRY_DAYS * ONE_DAY_MS

/** How often to refresh the cookie expiry on user activity */
const COOKIE_REFRESH_INTERVAL_MS = ONE_DAY_MS

/** Events that count as user activity for the sliding expiry & idle logout */
const ACTIVITY_EVENTS = ['mousedown', 'keydown'] as const

type TLegacyAuthState = {
  token?: Nullable<string>
  userId?: NullableID
}

/**
 * One-time migration for users who logged in before this change, when the token
 * still lived in IndexedDB (localforage). If there is no cookie yet but a legacy
 * entry exists, seed the cookie from it so those users stay logged in. The
 * legacy entry is then removed.
 */
const migrateLegacyTokenToCookie = async () => {
  const authCookie = readAuthCookie()

  // a cookie already exists – it is the newer source of truth
  if (isDefined(authCookie)) {
    return
  }

  const legacyStorageKey = `${appConfig.persistenceKey}_Auth`

  try {
    const legacyState = await localforage.getItem<Nullable<TLegacyAuthState>>(
      legacyStorageKey,
    )

    if (legacyState && legacyState.token) {
      const userId = legacyState.userId ?? null
      writeAuthCookie(legacyState.token, userId)
      logPersistence('migrated auth from IndexedDB to cookie')
    }

    await localforage.removeItem(legacyStorageKey)
  } catch (error) {
    logPersistence('auth migration failed', error)
  }
}

/**
 * Persists the auth model to a shareable cookie instead of IndexedDB. It
 * restores the token on startup, mirrors every change to the cookie (login
 * writes it, `invalidate()` removes it), keeps a sliding expiry alive on user
 * activity, and logs out an idle tab. Returns a disposer that tears it all down.
 */
export const persistAuthModel = async (auth: TAuthModel) => {
  await migrateLegacyTokenToCookie()

  const storedCookie = readAuthCookie()

  if (storedCookie) {
    auth.authenticate(storedCookie.token, storedCookie.userId ?? '')
    logPersistence('restored auth from cookie')
  }

  /**
   * Keep the cookie in sync with the in-memory auth state. MST calls this on
   * every change to `auth`, so a token means someone logged in (or we just
   * restored one above) and it gets written; no token means `invalidate()`
   * cleared it and the cookie has to go too, otherwise the next reload would
   * restore a session the user just ended.
   *
   * `onSnapshot` returns a disposer — call it to unsubscribe, or the callback
   * keeps writing cookies after this helper has been torn down.
   */
  const stopMirroringToCookie = onSnapshot(auth, ({ token, userId }) => {
    if (token) {
      writeAuthCookie(token, userId ?? null)
    } else {
      removeAuthCookie()
    }
  })

  // Keep the cookie alive with a sliding expiry on user activity, and log out
  // an idle tab after a long period of inactivity
  const refreshCookieExpiry = throttle(
    refreshAuthCookie,
    COOKIE_REFRESH_INTERVAL_MS,
  )

  let idleLogoutTimer: ReturnType<typeof setTimeout>

  // Reset the idle logout timer on user activity, and log out when it expires
  const restartIdleLogoutTimer = () => {
    clearTimeout(idleLogoutTimer)
    idleLogoutTimer = setTimeout(() => auth.invalidate(), AUTH_IDLE_TIMEOUT_MS)
  }

  const onUserActivity = () => {
    refreshCookieExpiry()
    restartIdleLogoutTimer()
  }

  // Start listening for user activity and start the idle logout timer
  // Passive event listeners are used to avoid blocking scrolling on mobile devices
  ACTIVITY_EVENTS.forEach(eventName =>
    window.addEventListener(eventName, onUserActivity, { passive: true }),
  )
  document.addEventListener('visibilitychange', onUserActivity)
  restartIdleLogoutTimer()

  // Return a disposer that cleans up all the listeners and timers when the auth model is no longer needed
  return () => {
    stopMirroringToCookie()
    refreshCookieExpiry.cancel()
    clearTimeout(idleLogoutTimer)

    ACTIVITY_EVENTS.forEach(eventName =>
      window.removeEventListener(eventName, onUserActivity),
    )
    document.removeEventListener('visibilitychange', onUserActivity)
  }
}
